import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';

const PEACOCK_SRC = 'https://BelvoirCastlePull.b-cdn.net/data-centre/peacock.png';
const NAVY = '#101d40';
const BRONZE = '#9d6d49';
const SLIDE_INTERVAL_MS = 5500;

const GlobalStyle = createGlobalStyle`
  body, html {
    background: transparent !important;
    margin: 0;
    padding: 0;
  }
`;

const dataFlow = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% { transform: translateY(200%); opacity: 0; }
`;

const gridPulse = keyframes`
  0% { opacity: 0.2; }
  50% { opacity: 0.5; }
  100% { opacity: 0.2; }
`;

const PageShell = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  background: #fff;
`;

const FormPanel = styled.div`
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3rem 1rem;

  @media (min-width: 640px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media (min-width: 1024px) {
    flex: none;
    padding-left: 5rem;
    padding-right: 5rem;
  }

  @media (min-width: 1280px) {
    padding-left: 6rem;
    padding-right: 6rem;
  }
`;

const FormInner = styled.div`
  width: 100%;
  max-width: 24rem;
  margin: 0 auto;

  @media (min-width: 1024px) {
    width: 24rem;
  }
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Peacock = styled.img`
  height: 3.5rem;
  width: 3.5rem;
  flex-shrink: 0;
  object-fit: contain;
`;

const BrandText = styled.div`
  line-height: 1.15;
`;

const BrandName = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${NAVY};
`;

const BrandSub = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: rgba(16, 29, 64, 0.65);
`;

const Heading = styled.h2`
  margin: 2rem 0 0;
  font-size: 1.5rem;
  line-height: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${NAVY};
`;

const Subcopy = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5rem;
  color: #57534e;
`;

const FormBlock = styled.div`
  margin-top: 2.5rem;
`;

const ErrorBox = styled.div`
  margin-bottom: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid #fecaca;
  border-left: 3px solid #ef4444;
  background: #fef2f2;
  padding: 0.75rem 0.875rem;
  font-size: 0.875rem;
  color: #b91c1c;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Field = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 500;
  color: #1c1917;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  margin-top: 0.5rem;
  border-radius: 0.375rem;
  background: #fff;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  color: #1c1917;
  outline: 1px solid #d6d3d1;
  outline-offset: -1px;
  border: none;
  box-sizing: border-box;

  &::placeholder {
    color: #a8a29e;
  }

  &:focus {
    outline: 2px solid ${NAVY};
    outline-offset: -2px;
  }

  &:disabled {
    opacity: 0.6;
  }

  @media (min-width: 640px) {
    font-size: 0.875rem;
    line-height: 1.5rem;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  width: 100%;
  justify-content: center;
  border-radius: 0.375rem;
  background: ${NAVY};
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 600;
  color: #fff;
  border: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #0c1836;
  }

  &:focus-visible {
    outline: 2px solid ${NAVY};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ShowcasePanel = styled.div`
  position: relative;
  display: none;
  flex: 1 1 0%;
  width: 0;
  background: ${NAVY};
  overflow: hidden;

  @media (min-width: 1024px) {
    display: block;
  }
`;

const BackdropRoot = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const GlowLayer = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(ellipse 70% 50% at 60% 30%, rgba(157, 109, 73, 0.28), transparent 55%),
    radial-gradient(ellipse 40% 35% at 20% 80%, rgba(16, 29, 64, 0.9), transparent 50%);
`;

const DataGrid = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  grid-template-rows: repeat(20, 1fr);
`;

const GridCell = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.035);
  animation: ${gridPulse} ${props => props.$duration}s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DataStream = styled.div`
  position: absolute;
  top: 0;
  opacity: 0;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  left: ${props => props.$left}%;
  background: ${props => props.$color};
  animation: ${dataFlow} ${props => props.$duration}s linear infinite;
  animation-delay: ${props => props.$delay}s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Particle = styled.div`
  position: absolute;
  border-radius: 50%;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  top: ${props => props.$top}%;
  left: ${props => props.$left}%;
  opacity: ${props => props.$opacity};
  background: ${props => props.$color};
`;

const ShowcaseContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 3rem;
`;

const ShowcaseInner = styled.div`
  position: relative;
  z-index: 10;
  max-width: 28rem;
`;

const SlideBody = styled.div`
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transform: translateY(${props => (props.$visible ? '0' : '0.5rem')});

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SlideLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${BRONZE};
`;

const SlideHeadline = styled.p`
  margin: 0.75rem 0 0;
  min-height: 5.5rem;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #fff;
  line-height: 1.3;
`;

const DotRow = styled.div`
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Dot = styled.button`
  height: 0.375rem;
  border: none;
  border-radius: 9999px;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  ${props =>
    props.$active
      ? css`
          width: 1.5rem;
          background: ${BRONZE};
        `
      : css`
          width: 0.375rem;
          background: rgba(255, 255, 255, 0.3);

          &:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `};
`;

const ShowcaseFooter = styled.p`
  margin: 1rem 0 0;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.55);
`;

function DataCentreBackdrop() {
  const particles = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        id: i,
        size: Math.random() * 10 + 4,
        top: Math.random() * 100,
        left: Math.random() * 100,
        opacity: Math.random() * 0.35 + 0.08,
        color: i % 2 === 0 ? 'rgba(255,255,255,0.18)' : 'rgba(157,109,73,0.35)',
      })),
    []
  );

  const streams = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        width: Math.random() * 2.5 + 1,
        height: Math.random() * 220 + 90,
        left: Math.random() * 100,
        duration: Math.random() * 10 + 6,
        delay: Math.random() * 5,
        color:
          i % 3 === 0
            ? 'rgba(157,109,73,0.55)'
            : i % 3 === 1
              ? 'rgba(255,255,255,0.28)'
              : 'rgba(184,150,120,0.4)',
      })),
    []
  );

  const gridCells = useMemo(
    () =>
      Array.from({ length: 400 }, (_, i) => ({
        id: i,
        duration: Math.random() * 4 + 2,
        delay: Math.random() * 2,
      })),
    []
  );

  return (
    <BackdropRoot aria-hidden="true">
      <GlowLayer />
      <DataGrid>
        {gridCells.map((cell) => (
          <GridCell key={cell.id} $duration={cell.duration} $delay={cell.delay} />
        ))}
      </DataGrid>
      {streams.map((stream) => (
        <DataStream
          key={stream.id}
          $width={stream.width}
          $height={stream.height}
          $left={stream.left}
          $duration={stream.duration}
          $delay={stream.delay}
          $color={stream.color}
        />
      ))}
      {particles.map((particle) => (
        <Particle
          key={particle.id}
          $size={particle.size}
          $top={particle.top}
          $left={particle.left}
          $opacity={particle.opacity}
          $color={particle.color}
        />
      ))}
    </BackdropRoot>
  );
}

function ProductShowcase({ slides, footerLabel }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    let fadeTimer;
    const timer = window.setInterval(() => {
      setVisible(false);
      fadeTimer = window.setTimeout(() => {
        setIndex((current) => (current + 1) % slides.length);
        setVisible(true);
      }, 280);
    }, SLIDE_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
      if (fadeTimer !== undefined) window.clearTimeout(fadeTimer);
    };
  }, [paused, slides.length]);

  const slide = slides[index];

  const goTo = (next) => {
    if (next === index) return;
    setVisible(false);
    window.setTimeout(() => {
      setIndex(next);
      setVisible(true);
    }, 200);
  };

  return (
    <ShowcaseInner
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <SlideBody $visible={visible} aria-live="polite">
        <SlideLabel>{slide.label}</SlideLabel>
        <SlideHeadline>{slide.headline}</SlideHeadline>
      </SlideBody>

      <DotRow role="tablist" aria-label="Product highlights">
        {slides.map((item, i) => (
          <Dot
            key={item.label}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Show ${item.label}`}
            onClick={() => goTo(i)}
            $active={i === index}
          />
        ))}
      </DotRow>

      <ShowcaseFooter>{footerLabel}</ShowcaseFooter>
    </ShowcaseInner>
  );
}

/**
 * Shared Belvoir admin login chrome (split panel + product showcase).
 * Auth logic stays in each page wrapper.
 */
function BelvoirAdminLoginLayout({
  productLabel,
  credentialsHint,
  slides,
  showcaseFooter,
  email,
  password,
  error,
  loading,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  emailId = 'admin-email',
  passwordId = 'admin-password',
}) {
  return (
    <>
      <GlobalStyle />
      <PageShell>
        <FormPanel>
          <FormInner>
            <div>
              <BrandRow role="img" aria-label={`Belvoir Castle ${productLabel}`}>
                <Peacock src={PEACOCK_SRC} alt="" aria-hidden="true" />
                <BrandText>
                  <BrandName>Belvoir Castle</BrandName>
                  <BrandSub>{productLabel}</BrandSub>
                </BrandText>
              </BrandRow>
              <Heading>Sign in to your account</Heading>
              <Subcopy>{credentialsHint}</Subcopy>
            </div>

            <FormBlock>
              {error && <ErrorBox role="alert">{error}</ErrorBox>}

              <Form onSubmit={onSubmit}>
                <Field>
                  <Label htmlFor={emailId}>Email address</Label>
                  <Input
                    id={emailId}
                    name="email"
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={onEmailChange}
                    disabled={loading}
                  />
                </Field>

                <Field>
                  <Label htmlFor={passwordId}>Password</Label>
                  <Input
                    id={passwordId}
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={onPasswordChange}
                    disabled={loading}
                  />
                </Field>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </SubmitButton>
              </Form>
            </FormBlock>
          </FormInner>
        </FormPanel>

        <ShowcasePanel>
          <DataCentreBackdrop />
          <ShowcaseContent>
            <ProductShowcase slides={slides} footerLabel={showcaseFooter} />
          </ShowcaseContent>
        </ShowcasePanel>
      </PageShell>
    </>
  );
}

export default BelvoirAdminLoginLayout;
